import { useEffect, useMemo, useState } from "react";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { authenticatedDownload } from "../lib/pdf";
import {
  TrendingUp, Download, Save, RefreshCw, Plus, Trash2, Loader2, Building2, Scale, BarChart3, Info, Mail,
} from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import ValuationCompsTable from "../components/valuation/ValuationCompsTable";
import CompLibraryPicker from "../components/valuation/CompLibraryPicker";
import { fmtUSDate } from "../lib/date";

const fmtUSD = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtPct = (n) => (n == null ? "—" : `${Number(n).toFixed(2)}%`);

const VIEWS = [
  { id: "property", label: "Per-property", icon: Building2 },
  { id: "portfolio", label: "Portfolio rollup", icon: BarChart3 },
  { id: "hypothetical", label: "Hypothetical", icon: Scale },
];

export default function Valuation() {
  const [view, setView] = useState("property");
  return (
    <DashboardShell>
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <div className="overline text-slate-500">Analytics</div>
            <h1
              className="text-3xl font-bold tracking-tight text-slate-900"
              style={{ fontFamily: "Outfit" }}
              data-testid="valuation-heading"
            >
              Property Valuation
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Blended value estimator — combines the income / cap-rate approach with a sales-comparable
              model. Optional <b>RentCast</b> integration pulls AVM + recent sales when enabled.
            </p>
          </div>
          <div className="inline-flex rounded-sm border border-slate-200 bg-white overflow-hidden">
            {VIEWS.map((v) => {
              const Icon = v.icon;
              const active = view === v.id;
              return (
                <button
                  key={v.id}
                  onClick={() => setView(v.id)}
                  data-testid={`valuation-view-${v.id}`}
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-semibold ${
                    active ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="w-4 h-4" /> {v.label}
                </button>
              );
            })}
          </div>
        </div>

        {view === "property" ? <PropertyValuation /> : view === "portfolio" ? <PortfolioValuation /> : <HypotheticalValuation />}
      </div>
    </DashboardShell>
  );
}

// ---------- PER-PROPERTY ----------
function PropertyValuation() {
  const [props, setProps] = useState([]);
  const [pid, setPid] = useState("");
  const [form, setForm] = useState({
    asset_class: "commercial",
    market_cap_rate: "",
    override_annual_rent: "",
    override_annual_expenses: "",
    override_sqft: "",
    income_weight: 0.6,
    use_rentcast: true,
    manual_comps: [],
    // Cost approach (residential)
    replacement_cost_per_sqft: "",
    land_value: "",
    effective_age_years: "",
    economic_life_years: 60,
  });
  const [result, setResult] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const selectedProp = props.find((p) => p.id === pid);
  const propType = selectedProp?.property_type || "";

  useEffect(() => {
    api.get("/properties").then((r) => {
      setProps(r.data || []);
      if (r.data && r.data.length > 0 && !pid) setPid(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pid) return;
    // Load any existing snapshots to populate "Value over time".
    api.get(`/properties/${pid}/valuations`).then((r) => setSnapshots(r.data?.items || []));
    setResult(null);
  }, [pid]);

  const run = async (save = false) => {
    if (!pid) return;
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const payload = {
      asset_class: form.asset_class || "commercial",
      market_cap_rate: form.market_cap_rate === "" ? null : Number(form.market_cap_rate),
      override_annual_rent: form.override_annual_rent === "" ? null : Number(form.override_annual_rent),
      override_annual_expenses: form.override_annual_expenses === "" ? null : Number(form.override_annual_expenses),
      override_sqft: form.override_sqft === "" ? null : Number(form.override_sqft),
      income_weight: Number(form.income_weight) || 0.6,
      use_rentcast: !!form.use_rentcast,
      manual_comps: form.manual_comps.filter((c) => c.address && c.sale_price && c.square_footage)
        .map((c) => ({
          address: String(c.address),
          sale_price: Number(c.sale_price),
          square_footage: Number(c.square_footage),
        })),
      replacement_cost_per_sqft: num(form.replacement_cost_per_sqft),
      land_value: num(form.land_value),
      effective_age_years: num(form.effective_age_years),
      economic_life_years: num(form.economic_life_years) || 60,
      save_snapshot: save,
    };
    save ? setBusySave(true) : setBusy(true);
    try {
      const { data } = await api.post(`/properties/${pid}/valuation`, payload);
      setResult(data);
      if (save) {
        toast.success("Valuation snapshot saved.");
        const { data: s } = await api.get(`/properties/${pid}/valuations`);
        setSnapshots(s.items || []);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Valuation failed.");
    } finally {
      setBusy(false);
      setBusySave(false);
    }
  };

  const downloadPdf = async () => {
    try {
      await authenticatedDownload(
        `/api/properties/${pid}/valuation/pdf${form.market_cap_rate ? `?market_cap_rate=${form.market_cap_rate}` : ""}`,
        `valuation-${pid}.pdf`,
      );
    } catch (e) {
      toast.error("PDF download failed.");
    }
  };

  const deleteSnap = async (sid) => {
    if (!confirm("Delete this snapshot?")) return;
    await api.delete(`/valuations/${sid}`);
    setSnapshots((prev) => prev.filter((s) => s.id !== sid));
  };

  const addComp = () =>
    setForm((f) => ({
      ...f,
      manual_comps: [...f.manual_comps, { address: "", sale_price: "", square_footage: "" }],
    }));
  const updateComp = (i, k, v) =>
    setForm((f) => ({
      ...f,
      manual_comps: f.manual_comps.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)),
    }));
  const removeComp = (i) =>
    setForm((f) => ({ ...f, manual_comps: f.manual_comps.filter((_, idx) => idx !== i) }));

  const onPickFromLibrary = (picked) => {
    setForm((f) => ({ ...f, manual_comps: [...f.manual_comps, ...picked] }));
  };

  const blended = result?.blended_value;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* LEFT: inputs */}
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="valuation-inputs">
          <div className="overline text-slate-500 mb-2">Property</div>
          <select
            className="input-clean w-full mb-4"
            value={pid}
            onChange={(e) => setPid(e.target.value)}
            data-testid="valuation-property-select"
          >
            {props.length === 0 && <option value="">Add a property first</option>}
            {props.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} {p.property_type ? `· ${p.property_type}` : ""}
              </option>
            ))}
          </select>

          <div className="overline text-slate-500 mb-1.5">Asset class</div>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { v: "commercial", l: "Commercial", sub: "Income approach dominant" },
              { v: "residential", l: "Residential", sub: "Sales-comp + cost dominant" },
            ].map((opt) => {
              const active = form.asset_class === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, asset_class: opt.v })}
                  className={`px-3 py-2 rounded-sm border text-left transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`prop-asset-class-${opt.v}`}
                >
                  <div className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{opt.l}</div>
                  <div className={`text-[10px] uppercase tracking-widest font-mono mt-0.5 ${active ? "text-emerald-300" : "text-slate-500"}`}>{opt.sub}</div>
                </button>
              );
            })}
          </div>

          <div className="overline text-slate-500 mb-2">Market cap rate</div>
          <div className="flex items-center gap-2 mb-4">
            <input
              type="number"
              step="0.1"
              placeholder="6.5"
              className="input-clean w-full"
              value={form.market_cap_rate}
              onChange={(e) => setForm({ ...form, market_cap_rate: e.target.value })}
              data-testid="valuation-cap-rate-input"
            />
            <span className="text-slate-500 text-sm">%</span>
          </div>

          <details className="mb-4">
            <summary className="overline text-slate-500 cursor-pointer">Overrides (what-if)</summary>
            <div className="space-y-2 mt-2">
              <Field label="Annual rent" value={form.override_annual_rent}
                onChange={(v) => setForm({ ...form, override_annual_rent: v })}
                testid="valuation-override-rent" prefix="$" />
              <Field label="Annual expenses" value={form.override_annual_expenses}
                onChange={(v) => setForm({ ...form, override_annual_expenses: v })}
                testid="valuation-override-expenses" prefix="$" />
              <Field label="Square footage" value={form.override_sqft}
                onChange={(v) => setForm({ ...form, override_sqft: v })}
                testid="valuation-override-sqft" />
            </div>
          </details>

          {/* Cost Approach (residential only) */}
          {form.asset_class === "residential" && (
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-sm p-3 mb-4" data-testid="prop-cost-approach-block">
              <div className="overline text-emerald-800 mb-2">Cost approach (residential)</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Replace $/sqft" value={form.replacement_cost_per_sqft}
                  onChange={(v) => setForm({ ...form, replacement_cost_per_sqft: v })}
                  prefix="$" testid="prop-rcn" />
                <Field label="Land value" value={form.land_value}
                  onChange={(v) => setForm({ ...form, land_value: v })}
                  prefix="$" testid="prop-land" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Effective age (yrs)" value={form.effective_age_years}
                  onChange={(v) => setForm({ ...form, effective_age_years: v })}
                  testid="prop-eff-age" />
                <Field label="Economic life (yrs)" value={form.economic_life_years}
                  onChange={(v) => setForm({ ...form, economic_life_years: v })}
                  testid="prop-econ-life" />
              </div>
              <div className="text-[10px] text-emerald-900/70 mt-2 font-mono uppercase tracking-widest">
                Optional. Leave blank to skip cost approach in the blend.
              </div>
            </div>
          )}

          <div className="overline text-slate-500 mb-1">Income / comp blend</div>
          <input
            type="range" min="0" max="1" step="0.05"
            value={form.income_weight}
            onChange={(e) => setForm({ ...form, income_weight: Number(e.target.value) })}
            className="w-full mb-1"
            data-testid="valuation-blend-slider"
          />
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-4">
            <span>Comp {Math.round((1 - form.income_weight) * 100)}%</span>
            <span>Income {Math.round(form.income_weight * 100)}%</span>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 mb-4" title="Requires RENTCAST_API_KEY in backend/.env">
            <input
              type="checkbox"
              checked={form.use_rentcast}
              onChange={(e) => setForm({ ...form, use_rentcast: e.target.checked })}
              data-testid="valuation-rentcast-toggle"
            />
            Pull RentCast AVM + comps
          </label>

          <div className="flex gap-2">
            <button
              onClick={() => run(false)}
              disabled={!pid || busy}
              className="btn-primary-dark flex-1 flex items-center justify-center gap-2"
              data-testid="valuation-run-button"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Calculate
            </button>
            <button
              onClick={() => run(true)}
              disabled={!pid || busySave || !result}
              className="btn-outline-dark flex items-center gap-2"
              data-testid="valuation-save-button"
              title="Save this run as a snapshot"
            >
              {busySave ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* MANUAL COMPS */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-slate-500">Manual comps</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPickerOpen(true)} type="button" className="text-xs font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1" data-testid="valuation-pull-library">
                📚 Library
              </button>
              <button onClick={addComp} type="button" className="text-xs font-semibold text-slate-900 hover:text-slate-700 flex items-center gap-1" data-testid="valuation-add-comp">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
          {form.manual_comps.length === 0 ? (
            <div className="text-xs text-slate-400">
              Add recent nearby sales to boost comp-approach accuracy.
            </div>
          ) : (
            <div className="space-y-2">
              {form.manual_comps.map((c, i) => (
                <div key={c._uid || i} className="border border-slate-200 rounded-sm p-2 space-y-1 relative" data-testid={`valuation-comp-${i}`}>
                  <input className="input-clean w-full text-xs" placeholder="Address"
                    value={c.address} onChange={(e) => updateComp(i, "address", e.target.value)} />
                  <div className="grid grid-cols-2 gap-1">
                    <input className="input-clean text-xs" placeholder="Sale price"
                      value={c.sale_price} onChange={(e) => updateComp(i, "sale_price", e.target.value)} />
                    <input className="input-clean text-xs" placeholder="SqFt"
                      value={c.square_footage} onChange={(e) => updateComp(i, "square_footage", e.target.value)} />
                  </div>
                  <button type="button" onClick={() => removeComp(i)} className="absolute top-1 right-1 text-slate-300 hover:text-red-600" aria-label="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: output */}
      <div className="lg:col-span-2 space-y-4">
        {!result ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-16 text-center text-slate-500" data-testid="valuation-empty">
            <Scale className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <div className="font-semibold text-slate-700">Run a valuation to see results</div>
            <div className="text-sm mt-1 max-w-md mx-auto">
              Pick a property, confirm the market cap rate, and click <b>Calculate</b>. We'll blend the income and sales-comp approaches into a single estimate.
            </div>
          </div>
        ) : (
          <>
            {/* HEADLINE */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-sm p-6" data-testid="valuation-result">
              <div className="overline text-white/60 mb-1">Blended estimated value</div>
              <div className="text-5xl font-bold font-mono tracking-tight" data-testid="valuation-blended-value">
                {fmtUSD(blended)}
              </div>
              <div className="text-white/70 text-sm mt-2">
                {result.property_name} · {result.property_address}
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <Kpi label="Income approach" value={fmtUSD(result.income_approach?.value)} />
                <Kpi label="Sales-comp approach" value={fmtUSD(result.comp_approach?.value)} />
                <Kpi label="NOI" value={fmtUSD(result.inputs?.noi)} />
                <Kpi label="Cap rate used" value={fmtPct(result.inputs?.market_cap_rate)} />
                <button
                  onClick={downloadPdf}
                  className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-sm ml-auto"
                  data-testid="valuation-pdf-button"
                >
                  <Download className="w-3.5 h-3.5" /> PDF report
                </button>
              </div>
            </div>

            {/* BREAKDOWN */}
            <div className={`grid gap-4 ${result.cost_approach?.value ? "md:grid-cols-3" : "md:grid-cols-2"}`}>
              <ApproachCard title="Income / cap rate" testid="approach-income"
                value={result.income_approach?.value} formula={result.income_approach?.formula} />
              <ApproachCard title="Sales comparables" testid="approach-comp"
                value={result.comp_approach?.value} formula={result.comp_approach?.formula}
                sub={result.comp_approach?.comps_used ? `${result.comp_approach.comps_used} comps · $${result.comp_approach.avg_price_per_sqft}/sqft avg` : "No comps yet"} />
              {result.cost_approach?.value && (
                <ApproachCard title="Cost approach" testid="approach-cost"
                  value={result.cost_approach.value} formula={result.cost_approach.formula}
                  sub={result.cost_approach.breakdown ? `Depreciation: ${result.cost_approach.breakdown.depreciation_pct}%` : null} />
              )}
            </div>

            {/* RentCast info */}
            {result.rentcast?.enabled && (
              <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="rentcast-section">
                <div className="overline text-slate-500 mb-2">RentCast AVM</div>
                <div className="text-2xl font-bold font-mono">{fmtUSD(result.rentcast.avm_value)}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Range {fmtUSD(result.rentcast.avm_range_low)} – {fmtUSD(result.rentcast.avm_range_high)}
                  {" · "}{result.rentcast.comp_count} comps
                  {result.rentcast.cached ? " · cached" : ""}
                </div>
              </div>
            )}
            {!result.rentcast?.enabled && form.use_rentcast && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="rentcast-disabled-hint">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <div>RentCast isn't configured. Add <code className="font-mono">RENTCAST_API_KEY</code> to <code className="font-mono">backend/.env</code> (free tier: 50 calls/month at rentcast.io/api) to pull live AVM + comps.</div>
              </div>
            )}

            {/* COMPS TABLE — shown inline with valuation result */}
            <ValuationCompsTable
              comps={result.all_comps}
              subjectSqft={result.square_footage}
              subjectPricePerSqft={
                result.square_footage && blended
                  ? Math.round(blended / result.square_footage)
                  : null
              }
              compAveragePpsf={result.comp_approach?.avg_price_per_sqft ?? null}
            />

            {/* SNAPSHOT HISTORY */}
            {snapshots.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="valuation-snapshots">
                <div className="overline text-slate-500 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5" /> Value over time ({snapshots.length} {snapshots.length === 1 ? "snapshot" : "snapshots"})
                </div>

                <ValuationSparkline snapshots={snapshots} />

                <ul className="divide-y divide-slate-100 mt-3 max-h-64 overflow-y-auto">
                  {snapshots.map((s) => (
                    <li key={s.id} className="py-2 flex items-center gap-3" data-testid={`snapshot-${s.id}`}>
                      <div className="flex-1">
                        <div className="font-mono text-sm">{fmtUSD(s.blended_value)}</div>
                        <div className="text-xs text-slate-500">
                          {fmtUSDate(s.created_at)} · NOI {fmtUSD(s.noi)} · {fmtPct(s.market_cap_rate)}
                          {s.source === "monthly_digest" ? " · from monthly digest" : ""}
                        </div>
                      </div>
                      <button onClick={() => deleteSnap(s.id)} className="text-slate-300 hover:text-red-600" aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
      <CompLibraryPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        propertyType={propType}
        onPick={onPickFromLibrary}
      />
    </div>
  );
}

function Field({ label, value, onChange, prefix, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-1">
        {prefix && <span className="text-slate-400 text-sm">{prefix}</span>}
        <input
          type="number"
          className="input-clean w-full"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
        />
      </div>
    </label>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="bg-white/10 rounded-sm px-3 py-1.5">
      <div className="overline text-white/60">{label}</div>
      <div className="font-mono font-bold text-sm">{value}</div>
    </div>
  );
}

function ApproachCard({ title, value, formula, sub, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid={testid}>
      <div className="overline text-slate-500 mb-1">{title}</div>
      <div className="text-2xl font-bold font-mono tracking-tight">{fmtUSD(value)}</div>
      {formula && <div className="text-xs text-slate-500 mt-1 font-mono">{formula}</div>}
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

function ValuationSparkline({ snapshots }) {
  const data = useMemo(
    () =>
      [...snapshots]
        .filter((s) => s.blended_value != null)
        .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
        .map((s) => ({
          date: (s.created_at || "").slice(0, 10),
          value: Number(s.blended_value || 0),
        })),
    [snapshots]
  );
  if (data.length < 2) {
    return (
      <div className="text-xs text-slate-400 py-4 text-center font-mono uppercase tracking-widest" data-testid="sparkline-too-few">
        Value-over-time chart unlocks once you have two or more snapshots (auto-saved on the 1st of each month).
      </div>
    );
  }
  const first = data[0].value;
  const last = data[data.length - 1].value;
  const delta = last - first;
  const pct = first > 0 ? ((delta / first) * 100).toFixed(1) : null;
  const up = delta >= 0;
  const tickFmt = (n) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`);
  return (
    <div data-testid="valuation-sparkline">
      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-3xl font-mono font-bold tracking-tight">{fmtUSD(last)}</div>
        <div className={`text-sm font-mono font-bold ${up ? "text-emerald-600" : "text-red-600"}`}>
          {up ? "▲" : "▼"} {fmtUSD(Math.abs(delta))}
          {pct != null && <span className="text-xs text-slate-400 ml-1">({up ? "+" : ""}{pct}%)</span>}
        </div>
        <div className="text-xs text-slate-400 ml-auto">since {fmtUSDate(data[0].date)}</div>
      </div>
      <div style={{ width: "100%", height: 120 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
            <YAxis
              tickFormatter={tickFmt}
              tick={{ fontSize: 10, fill: "#94a3b8" }}
              tickLine={false}
              axisLine={false}
              width={50}
              domain={["auto", "auto"]}
            />
            <Tooltip
              formatter={(v) => [fmtUSD(v), "Value"]}
              labelFormatter={(l) => l}
              contentStyle={{ fontSize: 11, borderRadius: 2, border: "1px solid #e2e8f0" }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={up ? "#059669" : "#dc2626"}
              strokeWidth={2}
              dot={{ r: 3, strokeWidth: 2 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


// ---------- PORTFOLIO ROLLUP ----------
function PortfolioValuation() {
  const [data, setData] = useState(null);
  const [mcap, setMcap] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const q = mcap ? `?market_cap_rate=${mcap}` : "";
      const { data } = await api.get(`/valuation/portfolio${q}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data) return <div className="text-slate-500 text-sm"><Loader2 className="w-4 h-4 animate-spin inline" /> Loading…</div>;
  const s = data.summary || {};

  const sendDigestNow = async () => {
    try {
      const { data: res } = await api.post("/valuation/digest/send");
      if (res.ok) {
        const delta = res.delta_total;
        const diff = delta == null
          ? "first digest saved"
          : (delta >= 0 ? `+$${Math.abs(delta).toLocaleString()} vs last month` : `-$${Math.abs(delta).toLocaleString()} vs last month`);
        toast.success(`Digest sent for ${res.month} — ${diff}`);
      } else {
        toast.info(`Digest skipped: ${res.skipped}`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Digest failed.");
    }
  };

  return (
    <div className="space-y-4" data-testid="portfolio-valuation">
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-3 flex-wrap">
        <div className="text-sm text-slate-700">Portfolio cap rate override (optional):</div>
        <input
          type="number"
          step="0.1"
          placeholder="e.g. 6.0"
          className="input-clean w-32"
          value={mcap}
          onChange={(e) => setMcap(e.target.value)}
          data-testid="portfolio-cap-override"
        />
        <span className="text-slate-500 text-sm">%</span>
        <div className="flex-1" />
        <button
          onClick={sendDigestNow}
          className="btn-outline-dark text-sm flex items-center gap-2"
          data-testid="portfolio-send-digest"
          title="Run the monthly valuation digest now + email it to you. Also fires automatically on the 1st of each month."
        >
          <Mail className="w-4 h-4" /> Send me the digest now
        </button>
        <button onClick={load} disabled={busy} className="btn-outline-dark text-sm flex items-center gap-2" data-testid="portfolio-refresh">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat testid="portfolio-total-value" label="Total value" value={fmtUSD(s.total_blended_value)} />
        <Stat testid="portfolio-total-noi" label="Total NOI" value={fmtUSD(s.total_noi)} />
        <Stat testid="portfolio-weighted-cap" label="Weighted cap rate" value={fmtPct(s.weighted_cap_rate)} />
        <Stat testid="portfolio-count" label="Properties" value={s.count || 0} />
      </div>

      <div className="border border-slate-200 rounded-sm overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="portfolio-table">
          <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Property</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-right px-4 py-3">Annual rent</th>
              <th className="text-right px-4 py-3">Annual expenses</th>
              <th className="text-right px-4 py-3">NOI</th>
              <th className="text-right px-4 py-3">Cap rate</th>
              <th className="text-right px-4 py-3">Blended value</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <tr key={it.property_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`portfolio-row-${it.property_id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-slate-900 truncate">{it.property_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{it.property_address}</div>
                </td>
                <td className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-slate-500">{it.property_type || "—"}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtUSD(it.annual_rent)}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtUSD(it.annual_expenses)}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtUSD(it.noi)}</td>
                <td className="px-4 py-3 text-right font-mono">{fmtPct(it.market_cap_rate)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{fmtUSD(it.income_value)}</td>
              </tr>
            ))}
            {data.items.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-400 text-sm">No properties to value yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="border border-slate-200 rounded-sm p-4 bg-white" data-testid={testid}>
      <div className="overline text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-bold font-mono tracking-tight text-slate-900">{value}</div>
    </div>
  );
}

// ---------- HYPOTHETICAL / MANUAL VALUATION ----------
function HypotheticalValuation() {
  const emptyForm = {
    label: "",
    address: "",
    asset_class: "commercial",
    property_type: "commercial",
    square_footage: "",
    annual_rent: "",
    annual_expenses: "",
    market_cap_rate: "6.5",
    income_weight: 0.6,
    use_rentcast: true,
    manual_comps: [],
    notes: "",
    // Residential
    bedrooms: "",
    bathrooms: "",
    lot_acres: "",
    year_built: "",
    condition: "",
    garage_spaces: "",
    has_basement: false,
    land_value: "",
    replacement_cost_per_sqft: "",
    effective_age_years: "",
    economic_life_years: 60,
  };
  const [form, setForm] = useState(emptyForm);
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const loadScenarios = async () => {
    try {
      const { data } = await api.get("/valuation/scenarios");
      setScenarios(data.items || []);
    } catch {
      setScenarios([]);
    }
  };
  useEffect(() => { loadScenarios(); }, []);

  const buildPayload = () => {
    const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    return {
      label: form.label.trim(),
      address: (form.address || "").trim(),
      asset_class: form.asset_class || "commercial",
      property_type: form.property_type || "commercial",
      square_footage: num(form.square_footage),
      annual_rent: Number(form.annual_rent || 0),
      annual_expenses: Number(form.annual_expenses || 0),
      market_cap_rate: Number(form.market_cap_rate || 0),
      income_weight: Number(form.income_weight || 0.6),
      use_rentcast: !!form.use_rentcast,
      manual_comps: form.manual_comps
        .filter((c) => c.address && c.sale_price && c.square_footage)
        .map((c) => ({
          address: String(c.address),
          sale_price: Number(c.sale_price),
          square_footage: Number(c.square_footage),
        })),
      notes: form.notes || "",
      // Residential
      bedrooms: num(form.bedrooms),
      bathrooms: num(form.bathrooms),
      lot_acres: num(form.lot_acres),
      year_built: num(form.year_built),
      condition: form.condition || null,
      garage_spaces: num(form.garage_spaces),
      has_basement: form.has_basement || null,
      land_value: num(form.land_value),
      replacement_cost_per_sqft: num(form.replacement_cost_per_sqft),
      effective_age_years: num(form.effective_age_years),
      economic_life_years: num(form.economic_life_years) || 60,
    };
  };

  const run = async () => {
    if (!form.label.trim()) {
      toast.error("Give this scenario a label (e.g. '150 Main St — acquisition').");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/valuation/scenarios/run", buildPayload());
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Calculation failed.");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.label.trim()) {
      toast.error("Give this scenario a label first.");
      return;
    }
    setBusySave(true);
    try {
      const { data } = await api.post("/valuation/scenarios", buildPayload());
      setResult(data.result);
      toast.success(`Scenario "${data.saved.label}" saved.`);
      loadScenarios();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed.");
    } finally {
      setBusySave(false);
    }
  };

  const loadScenario = (s) => {
    setForm({
      label: s.label || "",
      address: s.address || "",
      asset_class: s.asset_class || s.inputs?.asset_class || "commercial",
      property_type: s.property_type || "commercial",
      square_footage: s.inputs?.square_footage ?? "",
      annual_rent: s.inputs?.annual_rent ?? "",
      annual_expenses: s.inputs?.annual_expenses ?? "",
      market_cap_rate: s.inputs?.market_cap_rate ?? "6.5",
      income_weight: s.inputs?.income_weight ?? 0.6,
      use_rentcast: s.inputs?.use_rentcast !== false,
      manual_comps: s.inputs?.manual_comps || [],
      notes: s.inputs?.notes || "",
    });
    setResult(null);
    toast.info(`Loaded "${s.label}". Click Calculate to re-run with current market data.`);
  };

  const delScenario = async (sid) => {
    if (!confirm("Delete this scenario?")) return;
    await api.delete(`/valuation/scenarios/${sid}`);
    setScenarios((prev) => prev.filter((x) => x.id !== sid));
    toast.success("Scenario deleted.");
  };

  // Stable _uid per row — see top of file for rationale
  const _hypoUid = () => (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `h-${Date.now()}-${Math.random()}`;
  const addComp = () =>
    setForm((f) => ({
      ...f,
      manual_comps: [...f.manual_comps, { _uid: _hypoUid(), address: "", sale_price: "", square_footage: "" }],
    }));
  const updComp = (i, k, v) =>
    setForm((f) => ({ ...f, manual_comps: f.manual_comps.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmComp = (i) =>
    setForm((f) => ({ ...f, manual_comps: f.manual_comps.filter((_, idx) => idx !== i) }));

  const onPickFromLibrary = (picked) => {
    const stamped = picked.map((c) => ({ ...c, _uid: _hypoUid() }));
    setForm((f) => ({ ...f, manual_comps: [...f.manual_comps, ...stamped] }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="hypothetical-valuation">
      {/* LEFT: manual form */}
      <div className="lg:col-span-1 space-y-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="overline text-slate-500 mb-2">Scenario label *</div>
          <input
            className="input-clean w-full mb-3"
            placeholder="150 Main St — acquisition"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid="hypo-label-input"
          />

          <div className="overline text-slate-500 mb-1">Address (optional — unlocks RentCast)</div>
          <input
            className="input-clean w-full mb-3"
            placeholder="123 Main St, Anytown, NY 10001"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            data-testid="hypo-address-input"
          />

          {/* Asset-class toggle — drives which methodology dominates */}
          <div className="overline text-slate-500 mb-1.5">Asset class</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {[
              { v: "commercial", l: "Commercial", sub: "Income approach dominant" },
              { v: "residential", l: "Residential", sub: "Sales-comp dominant" },
            ].map((opt) => {
              const active = form.asset_class === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setForm({ ...form, asset_class: opt.v })}
                  className={`px-3 py-2 rounded-sm border text-left transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`hypo-asset-class-${opt.v}`}
                >
                  <div className="text-sm font-bold" style={{ fontFamily: "Outfit" }}>{opt.l}</div>
                  <div className={`text-[10px] uppercase tracking-widest font-mono mt-0.5 ${active ? "text-emerald-300" : "text-slate-500"}`}>{opt.sub}</div>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 mb-3">
            <label className="block">
              <div className="overline text-slate-500 mb-1">Type</div>
              <select
                className="input-clean w-full"
                value={form.property_type}
                onChange={(e) => setForm({ ...form, property_type: e.target.value })}
                data-testid="hypo-type-select"
              >
                {form.asset_class === "residential" ? (
                  <>
                    <option value="single_family">Single family</option>
                    <option value="duplex">Duplex</option>
                    <option value="triplex">Triplex</option>
                    <option value="fourplex">Fourplex</option>
                    <option value="condo">Condo</option>
                    <option value="townhouse">Townhouse</option>
                  </>
                ) : (
                  <>
                    <option value="commercial">Commercial</option>
                    <option value="mixed_use">Mixed use</option>
                    <option value="retail">Retail</option>
                    <option value="industrial">Industrial</option>
                    <option value="office">Office</option>
                    <option value="multifamily">Multifamily 5+</option>
                  </>
                )}
              </select>
            </label>
            <Field label="Square footage" value={form.square_footage}
              onChange={(v) => setForm({ ...form, square_footage: v })} testid="hypo-sqft" />
          </div>

          {/* Residential-only inputs */}
          {form.asset_class === "residential" && (
            <div className="bg-emerald-50/60 border border-emerald-200 rounded-sm p-3 mb-3">
              <div className="overline text-emerald-800 mb-2">Residential details</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Beds" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} testid="hypo-beds" />
                <Field label="Baths" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="hypo-baths" />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Lot (acres)" value={form.lot_acres} onChange={(v) => setForm({ ...form, lot_acres: v })} testid="hypo-lot-acres" />
                <Field label="Year built" value={form.year_built} onChange={(v) => setForm({ ...form, year_built: v })} testid="hypo-year-built" />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <label className="block">
                  <div className="overline text-slate-500 mb-1">Condition</div>
                  <select
                    className="input-clean w-full"
                    value={form.condition || ""}
                    onChange={(e) => setForm({ ...form, condition: e.target.value })}
                    data-testid="hypo-condition"
                  >
                    <option value="">—</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="average">Average</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </label>
                <Field label="Garage spaces" value={form.garage_spaces} onChange={(v) => setForm({ ...form, garage_spaces: v })} testid="hypo-garage" />
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 mb-3">
                <input type="checkbox" checked={!!form.has_basement} onChange={(e) => setForm({ ...form, has_basement: e.target.checked })} data-testid="hypo-basement" />
                Finished basement
              </label>

              <div className="overline text-emerald-800 mb-1.5">Cost approach (optional)</div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Replace $/sqft" value={form.replacement_cost_per_sqft} onChange={(v) => setForm({ ...form, replacement_cost_per_sqft: v })} prefix="$" testid="hypo-rcn" />
                <Field label="Land value" value={form.land_value} onChange={(v) => setForm({ ...form, land_value: v })} prefix="$" testid="hypo-land" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Effective age (yrs)" value={form.effective_age_years} onChange={(v) => setForm({ ...form, effective_age_years: v })} testid="hypo-eff-age" />
                <Field label="Economic life (yrs)" value={form.economic_life_years} onChange={(v) => setForm({ ...form, economic_life_years: v })} testid="hypo-econ-life" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mb-3">
            <Field label="Annual rent" value={form.annual_rent}
              onChange={(v) => setForm({ ...form, annual_rent: v })} prefix="$" testid="hypo-rent" />
            <Field label="Annual expenses" value={form.annual_expenses}
              onChange={(v) => setForm({ ...form, annual_expenses: v })} prefix="$" testid="hypo-expenses" />
          </div>

          <div className="mb-3">
            <div className="overline text-slate-500 mb-1">Market cap rate</div>
            <div className="flex items-center gap-2">
              <input
                type="number" step="0.1"
                className="input-clean w-full"
                value={form.market_cap_rate}
                onChange={(e) => setForm({ ...form, market_cap_rate: e.target.value })}
                data-testid="hypo-cap-rate"
              />
              <span className="text-slate-500 text-sm">%</span>
            </div>
          </div>

          <div className="overline text-slate-500 mb-1">Income / comp blend</div>
          <input
            type="range" min="0" max="1" step="0.05"
            value={form.income_weight}
            onChange={(e) => setForm({ ...form, income_weight: Number(e.target.value) })}
            className="w-full mb-1"
            data-testid="hypo-blend-slider"
          />
          <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-3">
            <span>Comp {Math.round((1 - form.income_weight) * 100)}%</span>
            <span>Income {Math.round(form.income_weight * 100)}%</span>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 mb-3">
            <input
              type="checkbox"
              checked={form.use_rentcast}
              onChange={(e) => setForm({ ...form, use_rentcast: e.target.checked })}
              data-testid="hypo-rentcast-toggle"
            />
            Pull RentCast AVM + comps
          </label>

          <textarea
            rows={2}
            className="input-clean w-full text-sm mb-3"
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="hypo-notes"
          />

          <div className="flex gap-2">
            <button
              onClick={run}
              disabled={busy}
              className="btn-primary-dark flex-1 flex items-center justify-center gap-2"
              data-testid="hypo-calculate-button"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Calculate
            </button>
            <button
              onClick={save}
              disabled={busySave || !form.label.trim()}
              className="btn-outline-dark flex items-center gap-2"
              data-testid="hypo-save-button"
              title="Save with the current label for later revisit"
            >
              {busySave ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {/* MANUAL COMPS */}
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-slate-500">Manual comps</div>
            <div className="flex items-center gap-2">
              <button onClick={() => setPickerOpen(true)} type="button" className="text-xs font-semibold text-slate-700 hover:text-slate-900 flex items-center gap-1" data-testid="hypo-pull-library">
                📚 Library
              </button>
              <button onClick={addComp} type="button" className="text-xs font-semibold text-slate-900 hover:text-slate-700 flex items-center gap-1" data-testid="hypo-add-comp">
                <Plus className="w-3 h-3" /> Add
              </button>
            </div>
          </div>
          {form.manual_comps.length === 0 ? (
            <div className="text-xs text-slate-400">Add 2–3 comparable sales to tighten the comp-approach estimate.</div>
          ) : (
            <div className="space-y-2">
              {form.manual_comps.map((c, i) => (
                <div key={c._uid || i} className="border border-slate-200 rounded-sm p-2 space-y-1 relative" data-testid={`hypo-comp-${i}`}>
                  <input className="input-clean w-full text-xs" placeholder="Address"
                    value={c.address} onChange={(e) => updComp(i, "address", e.target.value)} />
                  <div className="grid grid-cols-2 gap-1">
                    <input className="input-clean text-xs" placeholder="Sale price"
                      value={c.sale_price} onChange={(e) => updComp(i, "sale_price", e.target.value)} />
                    <input className="input-clean text-xs" placeholder="SqFt"
                      value={c.square_footage} onChange={(e) => updComp(i, "square_footage", e.target.value)} />
                  </div>
                  <button type="button" onClick={() => rmComp(i)} className="absolute top-1 right-1 text-slate-300 hover:text-red-600" aria-label="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT: output + saved scenarios */}
      <div className="lg:col-span-2 space-y-4">
        {!result ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-16 text-center text-slate-500" data-testid="hypo-empty">
            <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <div className="font-semibold text-slate-700">Value a property you don't own (yet)</div>
            <div className="text-sm mt-1 max-w-md mx-auto">
              Type in the rent, expenses, and cap rate for a property you're considering acquiring. We'll run the same income + sales-comp blend we use on your portfolio. Save it with a label for later.
            </div>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white rounded-sm p-6" data-testid="hypo-result">
              <div className="overline text-white/60 mb-1">Hypothetical value</div>
              <div className="text-5xl font-bold font-mono tracking-tight" data-testid="hypo-blended-value">
                {fmtUSD(result.blended_value)}
              </div>
              <div className="text-white/70 text-sm mt-2">
                {result.label}{result.address ? ` · ${result.address}` : ""}
              </div>
              <div className="mt-4 flex flex-wrap gap-3 text-xs">
                <Kpi label="Income approach" value={fmtUSD(result.income_approach?.value)} />
                <Kpi label="Sales-comp approach" value={fmtUSD(result.comp_approach?.value)} />
                <Kpi label="NOI" value={fmtUSD(result.inputs?.noi)} />
                <Kpi label="Cap rate" value={fmtPct(result.inputs?.market_cap_rate)} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <ApproachCard title="Income / cap rate" testid="approach-income"
                value={result.income_approach?.value} formula={result.income_approach?.formula} />
              <ApproachCard title="Sales comparables" testid="approach-comp"
                value={result.comp_approach?.value} formula={result.comp_approach?.formula}
                sub={result.comp_approach?.comps_used ? `${result.comp_approach.comps_used} comps · $${result.comp_approach.avg_price_per_sqft}/sqft avg` : "No comps yet"} />
            </div>

            {result.rentcast?.enabled && result.rentcast?.avm_value && (
              <div className="bg-white border border-slate-200 rounded-sm p-4">
                <div className="overline text-slate-500 mb-2">RentCast AVM (live)</div>
                <div className="text-2xl font-bold font-mono">{fmtUSD(result.rentcast.avm_value)}</div>
                <div className="text-xs text-slate-500 mt-1">
                  Range {fmtUSD(result.rentcast.avm_range_low)} – {fmtUSD(result.rentcast.avm_range_high)}
                  {" · "}{result.rentcast.comp_count} comps{result.rentcast.cached ? " · cached" : ""}
                </div>
              </div>
            )}

            {/* Comps table — same shape as on the live PropertyValuation view.
                Backend returns up to 20 comps (RentCast + manual) on every
                hypothetical run; surface them so users can verify which sales
                drove the comp-approach number. */}
            <ValuationCompsTable
              comps={result.all_comps}
              subjectSqft={result.square_footage}
              subjectPricePerSqft={
                result.square_footage && result.blended_value
                  ? Math.round(result.blended_value / result.square_footage)
                  : null
              }
              compAveragePpsf={result.comp_approach?.avg_price_per_sqft ?? null}
            />
          </>
        )}

        {/* SAVED SCENARIOS */}
        {scenarios.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-sm p-4" data-testid="hypo-scenarios-list">
            <div className="overline text-slate-500 mb-2 flex items-center gap-2">
              <Save className="w-3.5 h-3.5" /> Saved scenarios ({scenarios.length})
            </div>
            <ul className="divide-y divide-slate-100">
              {scenarios.map((s) => (
                <li key={s.id} className="py-2 flex items-center gap-3" data-testid={`hypo-scenario-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{s.label}</div>
                    <div className="text-xs text-slate-500 font-mono">
                      {fmtUSD(s.blended_value)} · {s.property_type || "—"} · {fmtUSDate(s.created_at)}
                      {s.address ? ` · ${s.address}` : ""}
                    </div>
                  </div>
                  <button onClick={() => loadScenario(s)} className="btn-outline-dark text-xs" data-testid={`hypo-load-${s.id}`}>Load</button>
                  <button onClick={() => delScenario(s.id)} className="text-slate-300 hover:text-red-600" aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <CompLibraryPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        propertyType={form.property_type}
        onPick={onPickFromLibrary}
      />
    </div>
  );
}
